import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { MenuOption } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { mergeRegister } from '@lexical/utils';
import { Box } from '@mui/material';
import {
  $createTextNode,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  COMMAND_PRIORITY_CRITICAL,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_UP_COMMAND,
  KEY_DOWN_COMMAND,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  KEY_TAB_COMMAND,
} from 'lexical';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormulaFunctionName } from '../../formula-engine';
import { FieldRefTarget } from '../../util/formula-vocabulary';
import { $enclosingCallsAtCaret, BoundName, EnclosingCall, namesInScope, slotKind } from './enclosingCalls';
import { $createFieldChipNode, fieldChipVariant } from './FieldChipNode';
import {
  $advanceAfterChip,
  browseText,
  FieldOption,
  matchFieldTrigger,
  optionsForQuery,
  renderFieldDetail,
  SOURCE_BADGE,
} from './FieldTypeaheadPlugin';
import {
  $functionInsertText,
  functionMenuItem,
  matchFunctionTrigger,
  offeredFunctions,
} from './FunctionTypeaheadPlugin';
import { isInsideStringLiteral } from './StringLiteralPlugin';
import { $tokenAtNode, caretRect, DocPopup, HintDoc, renderHintDoc, resolveDoc, TokenRef } from './tokenDocs';
import { TypeaheadMenu } from './TypeaheadMenu';

/** Where the caret sits, as far as Ctrl+Space cares. */
export type CaretHintPosition =
  /** No collapsed caret, or strictly inside an undocumentable token — nothing sane to hint. */
  | { kind: 'blocked' }
  /** An empty line, or beside a chip/token: no partial input to complete. */
  | { kind: 'neutral' }
  /** In plain text; `textBefore` is the anchor text up to the caret. */
  | { kind: 'text'; textBefore: string }
  /** Strictly inside a documentable token (a function name, a chip). */
  | { kind: 'token'; token: TokenRef };

export type HintContext =
  | { kind: 'none' }
  /** A hintable token — the automatic typeaheads own the spot. */
  | { kind: 'auto' }
  | { kind: 'menu'; names: BoundName[]; functions: FormulaFunctionName[]; fields: FieldOption[] }
  /** The caret's own token: document it (e.g. Ctrl+Space between "F" and "(" of "IF("). */
  | { kind: 'doc'; doc: HintDoc; nodeKey: string };

/**
 * What Ctrl+Space should do at the caret. Inside an unclosed "[query" or a
 * trailing identifier the automatic typeaheads already open on every editor
 * update, so the position is theirs; inside a completed token the token's own
 * doc is the hint; anywhere neutral, the on-demand menu offers the names in
 * scope, every function the position allows and what "[" would list there —
 * the records and root imports, or a narrow slot's own candidates — under the
 * same placement rules the automatic menus apply (none of either inside a
 * string literal).
 */
export function resolveHintContext(
  position: CaretHintPosition,
  calls: EnclosingCall[],
  fields: FieldRefTarget[]
): HintContext {
  if (position.kind === 'blocked') {
    return { kind: 'none' };
  }
  if (position.kind === 'token') {
    const doc = resolveDoc(position.token, fields);
    return doc ? { kind: 'doc', doc, nodeKey: position.token.nodeKey } : { kind: 'none' };
  }
  if (position.kind === 'text') {
    if (isInsideStringLiteral(position.textBefore)) {
      return { kind: 'none' };
    }
    if (matchFieldTrigger(position.textBefore) || matchFunctionTrigger(position.textBefore)) {
      return { kind: 'auto' };
    }
  }
  const names = slotKind(calls[0]) === 'free' ? namesInScope(calls) : [];
  const functions = offeredFunctions(calls);
  const options = optionsForQuery(fields, calls, '');
  return names.length || functions.length || options.length
    ? { kind: 'menu', names, functions, fields: options }
    : { kind: 'none' };
}

/**
 * The caret's position class. Token nodes (chips, function names, string
 * literals) are neutral at their edges — "right after the chip" is a place to
 * hint — while strictly inside, a documentable token is its own hint and
 * anything else (a string literal) is blocked.
 */
function $caretHintPosition(): CaretHintPosition {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
    return { kind: 'blocked' };
  }
  const anchor = selection.anchor;
  if (anchor.type !== 'text') {
    return { kind: 'neutral' };
  }
  const node = anchor.getNode();
  if (!$isTextNode(node)) {
    return { kind: 'neutral' };
  }
  if (node.isSimpleText()) {
    return { kind: 'text', textBefore: node.getTextContent().slice(0, anchor.offset) };
  }
  if (anchor.offset === 0 || anchor.offset === node.getTextContentSize()) {
    return { kind: 'neutral' };
  }
  const token = $tokenAtNode(node);
  return token ? { kind: 'token', token } : { kind: 'blocked' };
}

type Hint =
  | { kind: 'function'; name: FormulaFunctionName }
  | { kind: 'name'; bound: BoundName }
  | { kind: 'field'; option: FieldOption };

class HintOption extends MenuOption {
  constructor(readonly hint: Hint) {
    super(
      hint.kind === 'function'
        ? `fn:${hint.name}`
        : hint.kind === 'name'
          ? `name:${hint.bound.name}`
          : `field:${hint.option.target.path}`
    );
  }
}

interface HintOnDemandPluginProps {
  fields: FieldRefTarget[];
  onOpenChange?: (open: boolean) => void;
}

/**
 * Ctrl+Space: hints on demand. Inside a hintable token it re-sets the
 * selection so the automatic typeahead re-evaluates (reopening after an
 * Escape); at a neutral position it opens a one-shot menu of everything the
 * position allows. Any edit, caret move, Escape, or outside click closes the
 * menu — further typing hands over to the automatic typeaheads.
 */
type HintPopup =
  { type: 'menu'; options: HintOption[]; rect: DOMRect } | { type: 'doc'; content: ReactNode; rect: DOMRect };

export function HintOnDemandPlugin({ fields, onOpenChange }: HintOnDemandPluginProps) {
  const [editor] = useLexicalComposerContext();
  const [popup, setPopup] = useState<HintPopup | null>(null);
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback(() => {
    setPopup(null);
    onOpenChange?.(false);
  }, [onOpenChange]);

  const selectOption = useCallback(
    (option: HintOption) => {
      editor.update(() => {
        if (option.hint.kind === 'function' || option.hint.kind === 'name') {
          const node = $createTextNode(
            option.hint.kind === 'function' ? $functionInsertText(option.hint.name) : option.hint.bound.name
          );
          $insertNodes([node]);
          node.select();
        } else if (option.hint.option.navigate) {
          // "[Record." — the automatic field typeahead takes over with the record's keys.
          const node = $createTextNode(browseText(option.hint.option.target));
          $insertNodes([node]);
          node.select();
        } else {
          const { target } = option.hint.option;
          const chip = $createFieldChipNode(target.path, target.displayPath, fieldChipVariant(target));
          $insertNodes([chip]);
          chip.select();
          $advanceAfterChip(chip);
        }
      });
      close();
    },
    [editor, close]
  );

  useEffect(
    () =>
      editor.registerCommand<KeyboardEvent>(
        KEY_DOWN_COMMAND,
        (event) => {
          if (event.key !== ' ' || !event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
            return false;
          }
          event.preventDefault();
          const context = editor
            .getEditorState()
            .read(() => resolveHintContext($caretHintPosition(), $enclosingCallsAtCaret(), fields));
          if (context.kind === 'auto') {
            // Re-setting the selection forces the typeahead plugins' update
            // listener to re-evaluate, reopening a menu closed with Escape.
            editor.update(() => {
              const selection = $getSelection();
              if ($isRangeSelection(selection)) {
                $setSelection(selection.clone());
              }
            });
            return true;
          }
          if (context.kind === 'doc') {
            const rect =
              editor.getElementByKey(context.nodeKey)?.getBoundingClientRect() ??
              caretRect(editor.getRootElement());
            if (rect) {
              setPopup({ type: 'doc', content: renderHintDoc(context.doc), rect });
              onOpenChange?.(true);
            }
            return true;
          }
          if (context.kind === 'menu') {
            const rect = caretRect(editor.getRootElement());
            if (rect) {
              const options = [
                ...context.names.map((bound) => new HintOption({ kind: 'name', bound })),
                ...context.functions.map((name) => new HintOption({ kind: 'function', name })),
                ...context.fields.map((option) => new HintOption({ kind: 'field', option })),
              ];
              setHighlighted(0);
              setPopup({ type: 'menu', options, rect });
              onOpenChange?.(true);
            }
          }
          return true;
        },
        COMMAND_PRIORITY_CRITICAL
      ),
    [editor, fields, onOpenChange]
  );

  useEffect(() => {
    if (!popup) {
      return;
    }
    const options = popup.type === 'menu' ? popup.options : [];
    const choose = (event: KeyboardEvent | null): boolean => {
      event?.preventDefault();
      const option = options[highlighted];
      if (option) {
        selectOption(option);
      }
      return true;
    };
    const unregister = mergeRegister(
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        () => {
          close();
          return true;
        },
        COMMAND_PRIORITY_CRITICAL
      ),
      // One-shot: any edit or caret move closes; typing hands over to the
      // automatic typeaheads.
      editor.registerUpdateListener(() => close()),
      // Navigation only means something in the menu form; a doc popup lets
      // the keys keep editing.
      ...(popup.type === 'menu'
        ? [
            editor.registerCommand<KeyboardEvent>(
              KEY_ARROW_DOWN_COMMAND,
              (event) => {
                event.preventDefault();
                setHighlighted((index) => Math.min(index + 1, options.length - 1));
                return true;
              },
              COMMAND_PRIORITY_CRITICAL
            ),
            editor.registerCommand<KeyboardEvent>(
              KEY_ARROW_UP_COMMAND,
              (event) => {
                event.preventDefault();
                setHighlighted((index) => Math.max(index - 1, 0));
                return true;
              },
              COMMAND_PRIORITY_CRITICAL
            ),
            editor.registerCommand<KeyboardEvent | null>(
              KEY_ENTER_COMMAND,
              choose,
              COMMAND_PRIORITY_CRITICAL
            ),
            editor.registerCommand<KeyboardEvent>(KEY_TAB_COMMAND, choose, COMMAND_PRIORITY_CRITICAL),
          ]
        : [])
    );
    const onMouseDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        close();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      unregister();
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [popup, highlighted, editor, close, selectOption]);

  if (!popup) {
    return null;
  }
  if (popup.type === 'doc') {
    return (
      <DocPopup rect={popup.rect}>
        <div ref={containerRef}>{popup.content}</div>
      </DocPopup>
    );
  }
  return createPortal(
    <Box
      ref={containerRef}
      sx={{
        position: 'fixed',
        top: popup.rect.bottom + 4,
        left: popup.rect.left,
        zIndex: (theme) => theme.zIndex.modal + 10,
      }}
    >
      <TypeaheadMenu
        options={popup.options}
        toItem={(option) =>
          option.hint.kind !== 'field'
            ? functionMenuItem(option.hint)
            : {
                key: option.key,
                primary: option.hint.option.primary,
                badge: SOURCE_BADGE[option.hint.option.target.source],
                type: option.hint.option.target.type,
                detail: renderFieldDetail(option.hint.option.target),
              }
        }
        selectedIndex={highlighted}
        onHighlight={setHighlighted}
        onSelect={selectOption}
      />
    </Box>,
    document.body
  );
}
